import { DesignDto } from "../../generated/dashboard-client";
import { Button, Slider, Tooltip } from "antd";
import "./DesignCard.scss";
import EditDesignIcon from "../icons/EditDesignIcon";
import OrderPrintIcon from "../icons/OrderPrintIcon";
import DownloadDesignIcon from "../icons/DownloadDesignIcon";
import ChangeLocationIcon from "../icons/ChangeLocationIcon";
import DeleteDesignIcon from "../icons/DeleteDesignIcon";
import CheckIcon from "../icons/CheckIcon";
import MoveFolderIcon from "../icons/MoveFolderIcon";
import { env } from "../../env/env";
import {
  Dispatch,
  MouseEvent as ReactMouseEvent,
  SetStateAction,
  useEffect,
  useState,
} from "react";
import { getHeader, isA4, isCustomSize, isDigiboard, isDigital, isInHouseOnly, isSocial, getTimeAgo } from "../../util/utils";
import CopyDesignIcon from "../icons/CopyDesignIcon";
import MyBrandIcon from "../icons/MyBrandIcon";
import RemoveFromFolderIcon from "../icons/RemoveFromFolderIcon";
import { DesignSizes } from "../../util/constants";
import DesignApi from "../../api/DesignApi";
import {
  locationsState,
  memberState,
} from "../../state/atoms";
import { useRecoilState } from "recoil";
import ProIcon from "../icons/ProIcon";
import { LoadingOutlined } from "@ant-design/icons";
import { useRef } from "react";
import { useOnClickOutside } from "usehooks-ts";
import EditDesignLinkIcon from "../icons/EditDesignLinkIcon";
import { isDesignSubscription, isFreeUser } from "../../service/BillingService";
import { DownloadFileTypeSelect, SheetPrintOptions, useSheetPrint } from "./SheetPrintOptions";

export enum MenuMode {
  Root,
  Download,
}

export enum DownloadType {
  Standard,
  ProPrint,
  PremiumWeb,
  BasicImage,
  HDVideo,
}

const PremiumDownloadTypes = [
  DownloadType.Standard,
  DownloadType.ProPrint,
  DownloadType.PremiumWeb,
  DownloadType.HDVideo,
];

export enum DesignOption {
  Download,
  ChangeLocation,
  MoveToFolder,
  RemoveFromFolder,
  SaveAsBrandTemplate,
  Delete,
}

export interface DesignCardMenuProps {
  design: DesignDto;
  hasRemoveFolderOption: boolean;
  onOptionClick: (design: DesignDto, option: DesignOption) => void;
  mode: MenuMode;
  setMode: Dispatch<SetStateAction<MenuMode>>;
  isCopyLoading: boolean;
  onCopyDesign: () => void;
  closeMenu: () => void;
  hideDropdown: () => void;
  isInPrintProduction: boolean;
  setDesignInPrintProductionModal: (isVisible: boolean) => void;
  setDesignEditLinkModal: (isVisible: boolean) => void;
  editDesign: () => void;
  hasUsers: boolean;
  canSaveAsBrandTemplate: boolean;
  isSharedInListings: boolean;
}

export default function DesignCardMenu({
  mode,
  design,
  onOptionClick,
  hasRemoveFolderOption,
  setMode,
  isCopyLoading,
  onCopyDesign,
  closeMenu,
  hideDropdown,
  isInPrintProduction,
  setDesignEditLinkModal,
  editDesign,
  hasUsers,
  canSaveAsBrandTemplate,
  isSharedInListings
}: DesignCardMenuProps) {
  const ref = useRef(null);
  const [member] = useRecoilState(memberState);
  const [locations] = useRecoilState(locationsState);
  const [downloadType, setDownloadType] = useState(DownloadType.Standard);
  const [downloadLoading, setDownloadLoading] = useState(false);

  const [videoDuration, setVideoDuration] = useState(15);
  const sheetPrint = useSheetPrint(design);

  useEffect(() => {
    let type = DownloadType.Standard;
    if (isFreeUser(member)) {
      type = DownloadType.BasicImage;
    } else {
      if (isDigital(design) || isCustomSize(design)) {
        type = DownloadType.PremiumWeb;
      }
    }
    setDownloadType(type);
  }, [member, design]);

  const handleClickOutside = (ev: MouseEvent) => {
    closeMenu();
  }

  useOnClickOutside(ref, (ev) => handleClickOutside(ev as MouseEvent));

  function changeDownloadType(type: DownloadType, design: DesignDto, ev?: ReactMouseEvent) {
    if (isFreeUser(member) && !design.free) {
      if (PremiumDownloadTypes.includes(type)) {
        closeMenu();
        if (isDesignSubscription(member)) {
          window.MHM.ProModal.switchToProWithOverview({
            memberId: member.id,
            onSuccess: () => window.location.reload()
          });
        } else {
          window.MHM.ProModal.upgrade({
            memberId: member.id,
            onSuccess: () => window.location.reload()
          });
        }
        return;
      }
    }
    ev?.stopPropagation();
    setDownloadType(type);
  }

  function onOrderPrintClick() {
    window.location.href = `${env.timberContextPath}/design?id=${design.id}&open=print`;
  }

  function onCopyDesignClick(ev: ReactMouseEvent) {
    ev.stopPropagation();
    onCopyDesign();
  }

  function onGetEditLinkClick() {
    closeMenu();
    setDesignEditLinkModal(true);
  }

  function onDesignOptionClick(design: DesignDto, option: DesignOption) {
    closeMenu();
    onOptionClick(design, option);
  }

  function onDownloadDesignClick(ev: ReactMouseEvent) {
    ev.stopPropagation();
    if (design.upload) {
      setDownloadLoading(true);
      download();
    } else {
      setMode(MenuMode.Download);
    }
  }

  function startDownload(ev: ReactMouseEvent) {
    ev.stopPropagation();
    setDownloadLoading(true);
    download();
  }

  async function download() {
    let call = null;

    if (design.upload) {
      call = await DesignApi.downloadUploadedPdf(design.id!, design.name!);
    } else if (
      downloadType === DownloadType.Standard ||
      downloadType === DownloadType.ProPrint
    ) {
      await DesignApi.registerDownload(design.id!);
      call = await DesignApi.downloadPdf(
        design.id!,
        downloadType === DownloadType.ProPrint,
        downloadType === DownloadType.Standard ? sheetPrint.request : undefined
      );
    } else if (
      downloadType === DownloadType.BasicImage ||
      downloadType === DownloadType.PremiumWeb
    ) {
      await DesignApi.registerDownload(design.id!);
      call = await DesignApi.downloadImage(
        design.id!,
        downloadType === DownloadType.PremiumWeb ? "png" : "jpg"
      );
    } else if (downloadType === DownloadType.HDVideo) {
      call = await DesignApi.downloadVideo(design.id!, videoDuration);
    }

    if (call === null) {
      setDownloadLoading(false);
      return;
    }

    const blob = await call.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.style.display = "none";
    a.href = url;
    // @ts-ignore
    a.download = getHeader([...call.headers], "filename");
    if (design.upload) {
      a.download = design.name!;
    }
    document.body.appendChild(a);
    a.click();
    setDownloadLoading(false);
    window.URL.revokeObjectURL(url);
    hideDropdown();
  }

  function getSizeLabel(design: DesignDto) {
    if (isCustomSize(design)) {
      return "Custom";
    } else if (design.upload) {
      return "PDF Upload";
    } else {
      const data = DesignSizes[design.size ?? ""];
      return data ? data[0] : "";
    }
  }

  function getDimensions(design: DesignDto) {
    if (isCustomSize(design)) {
      return `${parseFloat(design.width ?? "")} x ${parseFloat(
        design.height ?? ""
      )}`;
    } else {
      const data = DesignSizes[design.size ?? ""];
      return data ? data[1] : "";
    }
  }

  const canDownloadPDF = (design: DesignDto) => {
    if (isSocial(design)) return false;
    if (isCustomSize(design)) {
      if (design.unit === 'px') {
        return false;
      }
    }
    return true;
  }

  const onChangeVideoDuration = (newValue: number) => {
    setVideoDuration(newValue);
  }

  const isViewOnly = !!member.viewOnly;

  const getPrintBtn = () => {
    const btn = <Button type={"link"} onClick={onOrderPrintClick} disabled={isCustomSize(design) || isDigital(design) || isA4(design) || !member.orderPrint}>
      <OrderPrintIcon />
      Order Prints
    </Button>;

    if (!member.orderPrint) {
      return <Tooltip placement="bottom" title={"You don't have permission to place print orders. Contact your account admin."}>{btn}</Tooltip>
    }
    return btn;
  }

  return (
    <div className={"ops-menu"} ref={ref}>
      <header>
        <h4 className={"mobile-display-name"}>{design.name}</h4>
        <h6>
          <span>{getSizeLabel(design)}</span>
          {!design.upload && <span> {getDimensions(design)}</span>}
          {!design.upload && design.creatorName && <span> &bull; By {design.creatorName}</span>}
          {!design.upload && design.updatedDate && <span> &bull; Edited {getTimeAgo(design)}</span>}
        </h6>
      </header>

      {mode === MenuMode.Root && (
        <ul>
          {!isViewOnly && <>
          {!design.upload &&
            <li>
              <Button type={"link"} onClick={editDesign}>
                <EditDesignIcon /> Edit This Design
              </Button>
            </li>}
          {!isCopyLoading && !design.upload && !member.itemLibraryOnly && !member.dataOnly && !member.updateDrd &&
            <li>
              <Button
                type={"link"}
                onClick={(ev) => onCopyDesignClick(ev)}
              >
                <CopyDesignIcon /> Make a copy
              </Button>
            </li>}
          {isCopyLoading && !member.itemLibraryOnly && !member.dataOnly && !member.updateDrd &&
            <li className={"copy-loading"}>
              <Button
                type={"link"}
              >
                <LoadingOutlined /> Make a copy
              </Button>
            </li>}
          {canSaveAsBrandTemplate && !design.upload &&
            <li>
              {isSharedInListings ? (
                <Tooltip
                  placement="bottom"
                  title="This menu is shared in Listings. Disconnect it there before saving it as a Brand Template."
                >
                  <Button type={"link"} disabled>
                    <MyBrandIcon /> Save as Brand Template
                  </Button>
                </Tooltip>
              ) : (
                <Button
                  type={"link"}
                  onClick={() => onDesignOptionClick(design, DesignOption.SaveAsBrandTemplate)}
                >
                  <MyBrandIcon /> Save as Brand Template
                </Button>
              )}
            </li>}
          {!design.upload && !isInHouseOnly(design) && ((!member.itemLibraryOnly && !member.dataOnly && !member.updateDrd) || member.orderPrint) &&
            <li>
              {!isInPrintProduction && getPrintBtn()}
              {isInPrintProduction &&
               <Button
                type={"link"}
                disabled={true}
              >
                <OrderPrintIcon />
                Printing...
              </Button>}
            </li>}
          </>}
          {!(downloadLoading && design.upload) && (member.downloads || member.itemLibraryOnly || member.dataOnly || member.updateDrd || member.viewOnly) &&
            <li>
              <Button type={"link"} onClick={onDownloadDesignClick}>
                <DownloadDesignIcon />
                Download
              </Button>
            </li>}
          {downloadLoading && design.upload &&
            <li>
              <Button type={"link"}>
                <LoadingOutlined /> Download
              </Button>
            </li>}
          {!isViewOnly && <>
          {locations.length > 1 && !member.itemLibraryOnly && !member.dataOnly && !member.updateDrd && (
            <li>
              {isSharedInListings ? (
                <Tooltip
                  placement="bottom"
                  title="This menu is shared in Listings. Disconnect it there before changing its location."
                >
                  <Button type={"link"} disabled>
                    <ChangeLocationIcon />
                    Change Location
                  </Button>
                </Tooltip>
              ) : (
                <Button
                  type={"link"}
                  onClick={() =>
                    onDesignOptionClick(design, DesignOption.ChangeLocation)
                  }
                >
                  <ChangeLocationIcon />
                  Change Location
                </Button>
              )}
            </li>
          )}
          {hasUsers && !member.itemLibraryOnly && !member.dataOnly && !member.updateDrd && <li>
            <Button
              type={"link"}
              onClick={() => onGetEditLinkClick()}
            >
              <EditDesignLinkIcon />
              Get Edit Link
            </Button>
          </li>}
          {!member.updateDrd && <li>
            <Button
              type={"link"}
              className={"add-to-folder"}
              onClick={() => onDesignOptionClick(design, DesignOption.MoveToFolder)}
            >
              <MoveFolderIcon />
              Move To Folder
            </Button>
          </li>}
          {hasRemoveFolderOption && !member.updateDrd && (
            <li>
              <Button
                type={"link"}
                className={"remove-from-folder"}
                onClick={() =>
                  onDesignOptionClick(design, DesignOption.RemoveFromFolder)
                }
              >
                <RemoveFromFolderIcon />
                Remove from Folder
              </Button>
            </li>
          )}
          {!member.itemLibraryOnly && !member.dataOnly && !member.updateDrd && <li>
            <Button
              type={"link"}
              onClick={() => onDesignOptionClick(design, DesignOption.Delete)}
            >
              <DeleteDesignIcon />
              Delete
            </Button>
          </li>}
          </>}
        </ul>
      )}

      {mode === MenuMode.Download && (
        <div className={"download-ops"}>
          {sheetPrint.options.length > 0 ? (
            <div className={"download-options"}>
              <DownloadFileTypeSelect
                design={design}
                downloadType={downloadType}
                onChange={(type) => changeDownloadType(type, design)}
                videoDuration={videoDuration}
                onVideoDurationChange={onChangeVideoDuration}
              />
              {downloadType === DownloadType.Standard && <SheetPrintOptions sheetPrint={sheetPrint} />}
            </div>
          ) : (
          <ul>
            {design.hasVideo &&
              <li>
                <Button
                  type={"link"}
                  onClick={(ev) =>
                    changeDownloadType(DownloadType.HDVideo, design, ev)
                  }
                >
                  HD Video
                  {(!isFreeUser(member)) && downloadType === DownloadType.HDVideo && <CheckIcon />}
                  {isFreeUser(member) && <ProIcon />}
                </Button>

                {downloadType === DownloadType.HDVideo &&
                  <div className="slider">
                    <div>Duration</div>
                    <Slider defaultValue={videoDuration} value={videoDuration} min={1} max={30} onChange={onChangeVideoDuration} />
                    <span>{videoDuration}s</span>
                  </div>
                }
              </li>
            }
            {canDownloadPDF(design) && (
              <li>
                <Button
                  type={"link"}
                  onClick={(ev) =>
                    changeDownloadType(DownloadType.Standard, design, ev)
                  }
                >
                  Standard PDF
                  {(!isFreeUser(member) || design.free) && downloadType === DownloadType.Standard && <CheckIcon />}
                  {isFreeUser(member) && !design.free && <ProIcon />}
                </Button>
              </li>
            )}
            {canDownloadPDF(design) && !isDigiboard(design) && (
              <li>
                <Button
                  type={"link"}
                  onClick={(ev) =>
                    changeDownloadType(DownloadType.ProPrint, design, ev)
                  }
                >
                  Pro Print PDF
                  {(!isFreeUser(member) || design.free) && downloadType === DownloadType.ProPrint && <CheckIcon />}
                  {isFreeUser(member) && !design.free && <ProIcon />}
                </Button>
              </li>
            )}
            <li>
              <Button
                type={"link"}
                onClick={(ev) =>
                  changeDownloadType(DownloadType.PremiumWeb, design, ev)
                }
              >
                Premium Web Image
                {(!isFreeUser(member) || design.free) && downloadType === DownloadType.PremiumWeb && <CheckIcon />}
                {isFreeUser(member) && !design.free && <ProIcon />}
              </Button>
            </li>
            <li>
              <Button
                type={"link"}
                onClick={(ev) =>
                  changeDownloadType(DownloadType.BasicImage, design, ev)
                }
              >
                Basic Image
                {isFreeUser(member) && !design.free && <span className={"free"}>Free</span>}
                {downloadType === DownloadType.BasicImage && <CheckIcon />}
              </Button>
            </li>
          </ul>
          )}

          <footer>
            <Button
              type={"primary"}
              className={"primary"}
              size={"large"}
              onClick={(ev) => startDownload(ev)}
              loading={downloadLoading}
            >
              Download
            </Button>
          </footer>
        </div>
      )}
    </div>
  );
}
